/* AIRHOUSE Wishlist 2.0.0 — Shopify Customer Account API, public OAuth client. */
(() => {
  'use strict';
  if (window.AirhouseWishlistStore) return;
  let settings;
  try { settings = JSON.parse(document.getElementById('ah-wishlist-config').textContent); } catch { return; }
  const cfg = settings.account;
  if (!cfg?.enabled) return;
  const GUEST = 'airhouse:wishlist:v1';
  const PREFIX = 'airhouse:wishlist:account:v2:';
  const SESSION = 'airhouse:wishlist:session:v2';
  const FLOW = 'airhouse:wishlist:oauth-flow:v2';
  const RESULT = 'airhouse:wishlist:oauth-result:v2';
  const RESUME = 'airhouse:wishlist:native-login:v2';
  const LOGOUT = 'airhouse:wishlist:logout:v2';
  const MAX_ITEMS = 500;
  const listeners = new Set();
  const query = 'query AirhouseWishlist($namespace: String!, $key: String!) { customer { id metafield(namespace: $namespace, key: $key) { type value compareDigest } } }';
  const mutation = 'mutation AirhouseWishlistSave($metafields: [MetafieldsSetInput!]!) { metafieldsSet(metafields: $metafields) { metafields { value type compareDigest } userErrors { code field message } } }';
  let owner = cleanId(cfg.customerId);
  let session = readJSON(sessionStorage, SESSION);
  let generation = 0;
  let discovery;
  let flushPromise;
  let identityPromise;
  let nativeCheckedAt = 0;
  let locked = false;
  let connecting = false;
  let syncing = false;
  let error = '';
  let started = false;
  let autoOpen = false;
  let retryTimer;

  function cleanId(value) { return /^\d{1,25}$/.test(String(value || '')) ? String(value) : null; }
  function fail(code) { return Object.assign(new Error(code), { code }); }
  function readJSON(storage, key) {
    try { return JSON.parse(storage.getItem(key) || 'null'); } catch { return null; }
  }
  function writeJSON(storage, key, value) {
    const serialized = JSON.stringify(value);
    if (storage.getItem(key) !== serialized) storage.setItem(key, serialized);
  }
  function dropSession() {
    session = null;
    try { sessionStorage.removeItem(SESSION); } catch {}
  }
  if (!owner || cleanId(session?.customerId) !== owner || session?.clientId !== cfg.clientId) {
    const previous = cleanId(session?.customerId);
    if (previous && previous !== owner) { try { localStorage.removeItem(PREFIX + previous); } catch {} }
    dropSession();
  }

  function normalize(value) {
    if (!value || typeof value !== 'object') return null;
    const handle = String(value.handle || '');
    if (!/^[\p{L}\p{N}][\p{L}\p{N}-]{0,254}$/u.test(handle)) return null;
    let image = '';
    try {
      const url = new URL(value.image, location.origin);
      if (value.image && ['https:', 'http:'].includes(url.protocol)) image = url.href;
    } catch {}
    return {
      handle, title: String(value.title || 'Продукт').slice(0, 350),
      vendor: String(value.vendor || '').slice(0, 150), image,
      price: Number.isFinite(value.price) && value.price >= 0 ? value.price : null,
      compareAt: Number.isFinite(value.compareAt) && value.compareAt >= 0 ? value.compareAt : null,
      addedAt: Number.isFinite(value.addedAt) && value.addedAt >= 0 ? value.addedAt : 0
    };
  }
  function cleanItems(values) {
    if (!Array.isArray(values)) return [];
    const seen = new Set();
    return values.map(normalize).filter(item => {
      if (!item || seen.has(item.handle) || seen.size >= MAX_ITEMS) return false;
      seen.add(item.handle); return true;
    });
  }
  function guest() { return cleanItems(readJSON(localStorage, GUEST)?.items); }
  function state(id = owner) {
    const raw = readJSON(localStorage, PREFIX + id);
    const pending = [];
    const prefix = PREFIX + id + ':op:';
    for (let index = 0; index < localStorage.length; index++) {
      const key = localStorage.key(index);
      if (key?.startsWith(prefix)) pending.push(readJSON(localStorage, key));
    }
    const valid = pending.filter(op =>
      op && typeof op.id === 'string' && ['add', 'remove', 'import'].includes(op.type) &&
      normalize({ handle: op.handle }) && Number.isFinite(op.at) && (op.type === 'remove' || normalize(op.product))
    ).sort((a, b) => a.at - b.at || a.id.localeCompare(b.id));
    return { version: 2, items: apply(cleanItems(raw?.items), valid), pending: valid };
  }
  function read() { return owner ? (locked ? [] : state().items) : guest(); }
  function status() {
    return Object.freeze({ mode: owner ? 'account' : 'guest', connected: !!(owner && session && !locked),
      syncing, connecting, error, pending: owner ? state().pending.length : 0 });
  }
  function emit() { for (const listener of listeners) { try { listener(read(), status()); } catch {} } }
  function random() {
    const bytes = new Uint8Array(32); crypto.getRandomValues(bytes);
    return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  }
  function safeReturn(value) {
    try {
      const url = new URL(value, location.origin);
      if (url.origin !== location.origin || !/^\/(?!\/)/.test(url.pathname)) return '/';
      for (const key of ['code', 'state', 'error', 'error_description', 'iss', 'ah_wishlist_auth', 'ah_wishlist_connect']) url.searchParams.delete(key);
      return url.pathname + url.search + url.hash;
    } catch { return '/'; }
  }
  function returnHere() { return safeReturn(location.href); }
  function apply(items, pending) {
    const result = new Map(cleanItems(items).map(item => [item.handle, item]));
    for (const op of pending) {
      if (op.type === 'remove') result.delete(op.handle);
      else if (op.type !== 'import' || !result.has(op.handle)) {
        const product = normalize(op.product);
        if (product) result.set(op.handle, product);
      }
    }
    const list = [...result.values()].sort((a, b) => b.addedAt - a.addedAt || a.handle.localeCompare(b.handle));
    if (list.length > MAX_ITEMS) throw fail('LIMIT');
    return list;
  }
  function importGuest() {
    if (!owner) return;
    const incoming = guest();
    if (!incoming.length) return;
    const current = state();
    for (const product of incoming) {
      if (!current.items.some(item => item.handle === product.handle) && !current.pending.some(op => op.handle === product.handle)) {
        queueOperation('import', product);
      }
    }
    // Every account operation was persisted independently before removing guest saves.
    const claimed = new Map(incoming.map(item => [item.handle, item.addedAt]));
    writeJSON(localStorage, GUEST, { version: 1, items: guest().filter(item => claimed.get(item.handle) !== item.addedAt) });
  }
  function setOwner(next) {
    if (next !== owner) {
      if (owner) { try { localStorage.removeItem(PREFIX + owner); } catch {} }
      owner = next; generation++;
      if (cleanId(session?.customerId) !== owner) dropSession();
    }
    locked = false;
    importGuest();
    emit();
  }
  function setItems(next) {
    const normalized = cleanItems(next);
    if (next.length > MAX_ITEMS) throw fail('LIMIT');
    if (!owner) writeJSON(localStorage, GUEST, { version: 1, items: normalized });
    else {
      if (locked) throw fail('IDENTITY');
      const current = state();
      const before = new Map(current.items.map(item => [item.handle, item]));
      const after = new Map(normalized.map(item => [item.handle, item]));
      const operations = [];
      for (const [handle, product] of after) if (!before.has(handle)) operations.push({ id: random(), type: 'add', handle, product });
      for (const handle of before.keys()) if (!after.has(handle)) operations.push({ id: random(), type: 'remove', handle });
      for (const op of operations) queueOperation(op.type, op.product || { handle: op.handle });
    }
    error = ''; emit();
    if (owner) void refresh(true);
  }
  function queueOperation(type, value) {
    const product = normalize(value);
    if (!owner || !product || locked) throw fail('IDENTITY');
    const current = state();
    if (type !== 'remove' && !current.items.some(item => item.handle === product.handle) && current.items.length >= MAX_ITEMS) throw fail('LIMIT');
    const at = Math.max(Date.now(), ...current.pending.map(op => op.at + 1));
    const op = { id: random(), type, handle: product.handle, at };
    if (type !== 'remove') op.product = product;
    // Separate keys prevent two browser tabs from overwriting each other's queue.
    writeJSON(localStorage, PREFIX + owner + ':op:' + op.id, op);
    for (const previous of current.pending) {
      if (previous.handle === product.handle) localStorage.removeItem(PREFIX + owner + ':op:' + previous.id);
    }
  }
  function toggle(product) {
    const normalized = normalize(product);
    if (!normalized) throw fail('PRODUCT');
    const current = read();
    const saved = current.some(item => item.handle === normalized.handle);
    if (owner) queueOperation(saved ? 'remove' : 'add', { ...normalized, addedAt: Date.now() });
    else {
      const next = saved ? current.filter(item => item.handle !== normalized.handle) : [{ ...normalized, addedAt: Date.now() }, ...current];
      if (next.length > MAX_ITEMS) throw fail('LIMIT');
      writeJSON(localStorage, GUEST, { version: 1, items: next });
    }
    error = ''; emit();
    if (owner) void refresh(true);
    return !saved;
  }
  function remove(handle) {
    if (owner) queueOperation('remove', { handle });
    else writeJSON(localStorage, GUEST, { version: 1, items: guest().filter(item => item.handle !== handle) });
    error = ''; emit();
    if (owner) void refresh(true);
  }
  async function request(url, options = {}) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    try { return await fetch(url, { ...options, signal: controller.signal }); }
    finally { clearTimeout(timeout); }
  }
  async function identity(force = false) {
    if (!force && Date.now() - nativeCheckedAt < 1000) return owner;
    if (identityPromise) return identityPromise;
    identityPromise = (async () => {
      const url = new URL(settings.root || '/', location.origin);
      url.searchParams.set('section_id', 'ah-wishlist-session');
      const response = await request(url.href, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'text/html' } });
      if (!response.ok) throw fail('IDENTITY');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const data = html.getElementById('ah-wishlist-session');
      if (!data) throw fail('SESSION_SECTION');
      let parsed;
      try { parsed = JSON.parse(data.textContent); } catch { throw fail('IDENTITY'); }
      if (!Object.hasOwn(parsed, 'customerId') || (parsed.customerId !== null && !cleanId(parsed.customerId))) throw fail('IDENTITY');
      nativeCheckedAt = Date.now();
      setOwner(cleanId(parsed.customerId));
      return owner;
    })().finally(() => { identityPromise = null; });
    return identityPromise;
  }
  function trustedEndpoint(value) {
    const url = new URL(value);
    if (url.protocol !== 'https:' || url.origin !== cfg.accountOrigin) throw fail('ENDPOINT');
    return url.href;
  }
  async function discover() {
    if (discovery) return discovery;
    const results = await Promise.all([
      request(new URL('/.well-known/openid-configuration', cfg.shopOrigin).href, { credentials: 'omit' }),
      request(new URL('/.well-known/customer-account-api', cfg.shopOrigin).href, { credentials: 'omit' })
    ]);
    if (results.some(response => !response.ok)) throw fail('DISCOVERY');
    const [oidc, api] = await Promise.all(results.map(response => response.json()));
    discovery = { authorize: trustedEndpoint(oidc.authorization_endpoint), token: trustedEndpoint(oidc.token_endpoint),
      graphql: trustedEndpoint(api.graphql_api), issuer: String(oidc.issuer || '') };
    if (!/^https:\/\//.test(discovery.issuer)) { discovery = null; throw fail('DISCOVERY'); }
    return discovery;
  }
  function nativeLogin(returnTo = returnHere()) {
    if (!window.AH_WISHLIST_AUTH_HEAD) throw fail('HEAD_HOOK');
    writeJSON(sessionStorage, RESUME, { returnTo: safeReturn(returnTo), createdAt: Date.now() });
    const url = new URL(cfg.loginUrl || '/customer_authentication/login', location.origin);
    if (url.origin !== location.origin) throw fail('LOGIN_URL');
    url.searchParams.set('return_to', '/?ah_wishlist_connect=1');
    location.assign(url.href);
  }
  async function connect(silent = false, returnTo = returnHere()) {
    if (connecting) return;
    if (!window.AH_WISHLIST_AUTH_HEAD) throw fail('HEAD_HOOK');
    connecting = true; error = ''; emit();
    try {
      const endpoints = await discover();
      const verifier = random();
      const challenge = btoa(String.fromCharCode(...new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier)))))
        .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
      const flow = { state: random(), nonce: random(), verifier, returnTo: safeReturn(returnTo),
        redirectUri: new URL(cfg.callbackPath, location.origin).href, createdAt: Date.now(), customerId: owner, silent };
      writeJSON(sessionStorage, FLOW, flow);
      const url = new URL(endpoints.authorize);
      for (const [key, value] of Object.entries({ client_id: cfg.clientId, response_type: 'code',
        scope: 'openid email customer-account-api:full', redirect_uri: flow.redirectUri,
        state: flow.state, nonce: flow.nonce, code_challenge: challenge, code_challenge_method: 'S256', locale: settings.locale || 'bg' })) url.searchParams.set(key, value);
      if (silent) url.searchParams.set('prompt', 'none');
      location.assign(url.href);
    } catch (cause) { connecting = false; error = cause.code || 'AUTH'; emit(); throw cause; }
  }
  async function login() {
    try {
      await identity(true);
      if (owner) await connect(false);
      else nativeLogin();
    } catch (cause) { error = cause.code || 'AUTH'; emit(); }
  }
  function decodeClaims(token) {
    try {
      const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const bytes = Uint8Array.from(atob(payload.padEnd(Math.ceil(payload.length / 4) * 4, '=')), c => c.charCodeAt(0));
      return JSON.parse(new TextDecoder().decode(bytes));
    } catch { throw fail('TOKEN'); }
  }
  async function tokenRequest(body) {
    const endpoints = await discover();
    const response = await request(endpoints.token, { method: 'POST', credentials: 'omit',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' }, body });
    if (!response.ok) {
      if (response.status === 400 || response.status === 401) throw fail('LOGIN_REQUIRED');
      throw fail('TOKEN');
    }
    const data = await response.json();
    if (typeof data.access_token !== 'string' || !data.access_token || !Number.isFinite(data.expires_in) || data.expires_in <= 0) throw fail('TOKEN');
    return data;
  }
  async function accessToken() {
    if (!session || cleanId(session.customerId) !== owner) throw fail('LOGIN_REQUIRED');
    if (session.expiresAt > Date.now() + 60000) return session.accessToken;
    if (!session.refreshToken) { dropSession(); throw fail('LOGIN_REQUIRED'); }
    const expected = generation;
    const data = await tokenRequest(new URLSearchParams({ grant_type: 'refresh_token', client_id: cfg.clientId, refresh_token: session.refreshToken }));
    if (expected !== generation) throw fail('IDENTITY_CHANGED');
    session = { ...session, accessToken: data.access_token, refreshToken: data.refresh_token || session.refreshToken,
      expiresAt: Date.now() + data.expires_in * 1000 };
    writeJSON(sessionStorage, SESSION, session);
    return session.accessToken;
  }
  async function graphql(queryText, variables, tokenOverride) {
    const endpoints = await discover();
    const token = tokenOverride || await accessToken();
    const response = await request(endpoints.graphql, { method: 'POST', credentials: 'omit', cache: 'no-store',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', Authorization: token },
      body: JSON.stringify({ query: queryText, variables }) });
    if (response.status === 401 || response.status === 403) { dropSession(); throw fail('LOGIN_REQUIRED'); }
    if (!response.ok) throw fail('NETWORK');
    const body = await response.json();
    if (body.errors?.length) throw fail(body.errors.some(e => ['UNAUTHENTICATED', 'ACCESS_DENIED'].includes(e.extensions?.code)) ? 'LOGIN_REQUIRED' : 'GRAPHQL');
    return body.data;
  }
  async function remote(token) {
    const data = await graphql(query, { namespace: cfg.namespace, key: cfg.key }, token);
    const id = cleanId(data?.customer?.id?.split('/').at(-1));
    if (!id) throw fail('LOGIN_REQUIRED');
    const field = data.customer.metafield;
    let items = [];
    if (field) {
      let raw;
      try { raw = JSON.parse(field.value); } catch { throw fail('REMOTE_FORMAT'); }
      if (field.type !== 'json' || raw?.version !== 2 || !Array.isArray(raw.items) || raw.items.length > MAX_ITEMS ||
        typeof field.compareDigest !== 'string') throw fail('REMOTE_FORMAT');
      items = cleanItems(raw.items);
      if (items.length !== raw.items.length) throw fail('REMOTE_FORMAT');
    }
    return { id, items, digest: field?.compareDigest ?? null };
  }
  async function callback() {
    const result = readJSON(sessionStorage, RESULT);
    if (!result) return false;
    sessionStorage.removeItem(RESULT);
    const flow = readJSON(sessionStorage, FLOW);
    sessionStorage.removeItem(FLOW);
    if (!flow || !result.state || result.state !== flow.state || Date.now() - flow.createdAt > 600000 ||
      Date.now() - result.receivedAt > 60000 || flow.redirectUri !== new URL(cfg.callbackPath, location.origin).href) throw fail('AUTH_STATE');
    const endpoints = await discover();
    if (result.iss && result.iss !== endpoints.issuer) throw fail('AUTH_STATE');
    if (result.error) throw fail(result.error === 'login_required' ? 'LOGIN_REQUIRED' : 'AUTH');
    const data = await tokenRequest(new URLSearchParams({ grant_type: 'authorization_code', client_id: cfg.clientId,
      redirect_uri: flow.redirectUri, code: result.code, code_verifier: flow.verifier }));
    // Tokens come directly from the trusted HTTPS token endpoint. Identity is
    // independently obtained from Shopify's authenticated customer query below.
    const claims = decodeClaims(data.id_token || '');
    const audience = Array.isArray(claims.aud) ? claims.aud : [claims.aud];
    if (claims.nonce !== flow.nonce || claims.iss !== endpoints.issuer || !audience.includes(cfg.clientId) ||
      !Number.isFinite(claims.exp) || claims.exp * 1000 < Date.now() - 60000) throw fail('TOKEN');
    const customer = await remote(data.access_token);
    const authenticatedSession = { clientId: cfg.clientId, customerId: customer.id, accessToken: data.access_token,
      refreshToken: data.refresh_token || '', expiresAt: Date.now() + data.expires_in * 1000 };
    session = authenticatedSession;
    writeJSON(sessionStorage, SESSION, session);
    await identity(true);
    if (!owner || owner !== customer.id) {
      // Customer Account API tokens don't set the Online Store session cookie.
      // Establish the native storefront session once and compare identities again.
      session = authenticatedSession;
      writeJSON(sessionStorage, SESSION, session);
      nativeLogin(flow.returnTo);
      return true;
    }
    importGuest();
    await refresh(false);
    const target = safeReturn(flow.returnTo);
    if (target !== returnHere()) {
      writeJSON(sessionStorage, RESUME, { returnTo: target, createdAt: Date.now(), openOnly: true });
      location.replace(target);
    }
    else { autoOpen = true; emit(); }
    return true;
  }
  async function refresh(authorize = false) {
    if (flushPromise) return flushPromise;
    flushPromise = (async () => {
      syncing = true; error = ''; emit();
      try {
        await identity(true);
        if (!owner) return;
        if (!session) {
          if (authorize) await connect(true);
          return;
        }
        const expected = generation;
        const expectedOwner = owner;
        for (let round = 0; round < 8; round++) {
          const source = await remote();
          if (expected !== generation || source.id !== expectedOwner) throw fail('IDENTITY_CHANGED');
          const local = state(expectedOwner);
          const batch = local.pending;
          let confirmed = source.items;
          if (batch.length) {
            const next = apply(source.items, batch);
            const data = await graphql(mutation, { metafields: [{ ownerId: 'gid://shopify/Customer/' + expectedOwner,
              namespace: cfg.namespace, key: cfg.key, type: 'json', value: JSON.stringify({ version: 2, items: next }), compareDigest: source.digest }] });
            if (expected !== generation) throw fail('IDENTITY_CHANGED');
            const errors = data?.metafieldsSet?.userErrors;
            if (!Array.isArray(errors)) throw fail('GRAPHQL');
            if (errors.length) {
              if (errors.every(e => /COMPARE_DIGEST|STALE_OBJECT/.test(e.code || '')) && round < 7) continue;
              throw fail('SAVE');
            }
            const savedField = data.metafieldsSet.metafields?.[0];
            let saved;
            try { saved = JSON.parse(savedField.value); } catch { throw fail('SAVE'); }
            if (saved?.version !== 2 || !Array.isArray(saved.items)) throw fail('SAVE');
            confirmed = cleanItems(saved.items);
          }
          writeJSON(localStorage, PREFIX + expectedOwner, { version: 2, items: confirmed });
          for (const op of batch) localStorage.removeItem(PREFIX + expectedOwner + ':op:' + op.id);
          const remaining = state(expectedOwner).pending;
          emit();
          if (!remaining.length) return;
        }
        throw fail('SAVE');
      } catch (cause) {
        error = cause.code || 'NETWORK';
        if (error === 'LOGIN_REQUIRED') dropSession();
        if (error === 'IDENTITY_CHANGED') { dropSession(); locked = true; }
      } finally { syncing = false; connecting = false; emit(); }
    })().finally(() => { flushPromise = null; });
    return flushPromise;
  }
  async function logout() {
    try { if (session) await refresh(false); } catch {}
    if (owner) { try { localStorage.removeItem(PREFIX + owner); } catch {} }
    dropSession(); owner = null; generation++; locked = false; error = '';
    try { localStorage.setItem(LOGOUT, random()); } catch {}
    emit();
    const url = new URL(cfg.logoutUrl || '/account/logout', location.origin);
    if (url.origin === location.origin) location.assign(url.href);
  }
  async function start() {
    if (started) return;
    started = true;
    try {
      if (!window.AH_WISHLIST_AUTH_HEAD) throw fail('HEAD_HOOK');
      if (await callback()) return;
      const url = new URL(location.href);
      const resume = readJSON(sessionStorage, RESUME);
      const connectingFromNative = url.searchParams.get('ah_wishlist_connect') === '1';
      if (connectingFromNative) {
        url.searchParams.delete('ah_wishlist_connect');
        history.replaceState(history.state, '', url.pathname + url.search + url.hash);
      }
      if (resume && Date.now() - resume.createdAt < 600000) {
        sessionStorage.removeItem(RESUME);
        await identity(true);
        if (resume.openOnly) { autoOpen = true; await refresh(false); }
        else if (!owner) throw fail('NATIVE_LOGIN');
        else if (session?.customerId === owner) {
          await refresh(false);
          const target = safeReturn(resume.returnTo);
          if (target !== returnHere()) {
            writeJSON(sessionStorage, RESUME, { returnTo: target, createdAt: Date.now(), openOnly: true });
            location.replace(target);
          } else autoOpen = true;
        } else await connect(true, resume.returnTo);
      } else {
        importGuest();
        if (owner && session) await refresh(false);
      }
    } catch (cause) { error = cause.code || 'AUTH'; connecting = false; emit(); }
    finally { emit(); }
  }
  async function opened() {
    autoOpen = false;
    try {
      await identity(true);
      await refresh(true);
    } catch (cause) { error = cause.code || 'NETWORK'; emit(); }
  }
  window.addEventListener('storage', event => {
    if (event.key === LOGOUT || event.key === null) {
      if (owner) { try { localStorage.removeItem(PREFIX + owner); } catch {} }
      dropSession(); owner = null; generation++; locked = false; nativeCheckedAt = 0;
      emit(); return;
    }
    if (event.key === GUEST || event.key === PREFIX + owner || event.key?.startsWith(PREFIX + owner + ':op:')) {
      emit();
      if (owner && session) {
        clearTimeout(retryTimer);
        retryTimer = setTimeout(() => { void refresh(false); }, 150);
      }
    }
  });
  window.addEventListener('online', () => { if (owner && session) void refresh(false); });
  window.addEventListener('pageshow', event => {
    if (event.persisted) { locked = !!owner; emit(); void identity(true).then(() => refresh(false)).catch(() => { error = 'IDENTITY'; emit(); }); }
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && owner) {
      locked = true; emit();
      void identity(true).then(() => refresh(false)).catch(() => { error = 'IDENTITY'; emit(); });
    }
  });
  window.AirhouseWishlistStore = Object.freeze({ version: '2.0.0', read, setItems, toggle, remove, status, start, opened, login, logout,
    refresh: () => refresh(true), get shouldOpen() { return autoOpen; },
    subscribe(listener) { listeners.add(listener); return () => listeners.delete(listener); }
  });
})();
